import {
  TRAY_CASING_COLOR,
  TRAY_CASING_OPACITY,
  TRAY_DIMMED_FILL_OPACITY,
  TRAY_DIMMED_WALL_OPACITY,
  TRAY_FILL_COLOR,
  TRAY_FILL_OPACITY,
  TRAY_HIGHLIGHT_FILL_OPACITY,
  TRAY_HIGHLIGHT_WALL_COLOR,
  TRAY_HIGHLIGHT_WALL_OPACITY,
  TRAY_MIN_WALL_WIDTH,
  TRAY_WALL_COLOR,
  TRAY_WALL_OPACITY,
  TRAY_WALL_WIDTH,
  TRAY_WIDTH,
} from "./constants";
import { trayWalls } from "./trayWalls";
import type { CableTrayLayerProps } from "./types";

export const CableTrayLayer = ({
  trays,
  strokeScale = 1,
  highlightType = null,
}: CableTrayLayerProps) => {
  if (trays.length === 0) return null;

  const width = TRAY_WIDTH * strokeScale;
  const wallWidth = Math.max(
    TRAY_WALL_WIDTH * strokeScale,
    TRAY_MIN_WALL_WIDTH,
  );

  return (
    <g data-testid="cable-tray-layer" className="pointer-events-none">
      {trays.map((tray, index) => {
        const isHighlighted =
          highlightType !== null && tray.type === highlightType;
        const isDimmed = highlightType !== null && !isHighlighted;
        const wallOpacity = isDimmed
          ? TRAY_DIMMED_WALL_OPACITY
          : isHighlighted
            ? TRAY_HIGHLIGHT_WALL_OPACITY
            : TRAY_WALL_OPACITY;
        const fillOpacity = isDimmed
          ? TRAY_DIMMED_FILL_OPACITY
          : isHighlighted
            ? TRAY_HIGHLIGHT_FILL_OPACITY
            : TRAY_FILL_OPACITY;
        const walls = trayWalls({
          from: tray.from,
          to: tray.to,
          halfWidth: width / 2,
        });
        return (
          // By index: a tray has no id of its own, and this layer draws them all
          // every render — nothing is reordered or kept between renders.
          <g key={index}>
            {/* The casing lifts the whole lane off the drawing; the tint says
                a zone is here. Both translucent, so the architect's own tray
                band still reads through the one we draw over it. */}
            <line
              x1={tray.from.x}
              y1={tray.from.y}
              x2={tray.to.x}
              y2={tray.to.y}
              stroke={TRAY_CASING_COLOR}
              strokeWidth={width + wallWidth * 2}
              strokeOpacity={isDimmed ? 0 : TRAY_CASING_OPACITY}
            />
            <line
              x1={tray.from.x}
              y1={tray.from.y}
              x2={tray.to.x}
              y2={tray.to.y}
              stroke={TRAY_FILL_COLOR}
              strokeWidth={width}
              strokeOpacity={fillOpacity}
            />
            {walls.map((wall, side) => (
              <line
                key={side}
                x1={wall.from.x}
                y1={wall.from.y}
                x2={wall.to.x}
                y2={wall.to.y}
                stroke={
                  isHighlighted ? TRAY_HIGHLIGHT_WALL_COLOR : TRAY_WALL_COLOR
                }
                strokeWidth={wallWidth}
                strokeOpacity={wallOpacity}
                strokeLinecap="round"
              />
            ))}
          </g>
        );
      })}
    </g>
  );
};
